import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoSave, saisieDateIncomplete } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import OrganizerEmailModeField from '../components/registrations/OrganizerEmailModeField';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, Loader2, AlertTriangle, DoorOpen, Lock } from 'lucide-react';
import apiService from '../services/api.service';
import CustomFieldBuilder, { CustomField } from '../components/CustomFieldBuilder';
import RegistrationCategoryBuilder, { RegistrationCategory } from '../components/RegistrationCategoryBuilder';
import type { CommissionPayer } from '../components/PriceQuote';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import SlotsDialog from '../components/heats/dialogs/SlotsDialog';
import type { Heat } from '../types/heats';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { DateTimeInput } from '../components/ui/date-time-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../components/ui/tooltip';

// Composant réutilisable qui accepte eventId en prop.
// `section` est piloté par les onglets du haut (Général / Catégories / Questions & décharge)
// pour éviter les onglets-dans-onglets ; la sauvegarde reste unique pour tout.
export const RegistrationSettingsContent: React.FC<{ eventId: string; embedded?: boolean; section?: 'general' | 'categories' | 'questions' }> = ({ eventId, embedded = false, section = 'general' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);

  const [formData, setFormData] = useState({
    registration_open: false,
    registration_start_date: null as string | null,
    registration_end_date: null as string | null,
    max_global_participants: null as number | null,
    show_registration_count: true,  // ✅ Point 3: Contrôle affichage compteur inscriptions
    // Places restantes sous chaque catégorie limitée ; seuil null = toujours.
    show_category_spots: false,
    category_spots_threshold: null as number | null,
    require_waiver: false,
    waiver_text: '',
    custom_fields: [] as CustomField[],
    allow_participant_editing: false,
    slot_selection_enabled: false,
    participant_editing_deadline: null as string | null,
    event_instagram: '',
  });

  const [categories, setCategories] = useState<RegistrationCategory[]>([]);

  // Qui paie la commission LogFit® : un geste à part, il convertit les prix
  // de toutes les catégories (PUT /commission-payer).
  const [commissionPayer, setCommissionPayer] = useState<CommissionPayer>('athlete');
  const [commissionLocked, setCommissionLocked] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  // Changer de mode réécrit des prix affichés au public : on montre d'abord
  // ce qui va changer, l'organisateur confirme (PUT avec preview, puis sans).
  const [pendingPayer, setPendingPayer] = useState<{
    payer: CommissionPayer;
    conversions: { name: string; old_price_cents: number; new_price_cents: number }[];
  } | null>(null);

  // L'écran s'enregistre au fur et à mesure : un interrupteur écrit tout de
  // suite, un champ texte au blur. Les refs évitent les closures périmées —
  // `commit` est mémoïsé, il ne doit pas capturer un formData d'il y a trois
  // rendus.
  const formRef = useRef(formData);
  formRef.current = formData;
  const isNewRef = useRef(isNew);
  isNewRef.current = isNew;
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  // `saveCategories` est un `const` défini plus bas — le lire ici lèverait un
  // ReferenceError. La ref est posée vide et alimentée juste après sa
  // définition : `auto` n'a besoin de la fonction qu'à l'exécution.
  const saveCategoriesRef = useRef<(liste: RegistrationCategory[]) => Promise<void>>(
    async () => {},
  );

  const auto = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    if (!eventId) return;

    // Les catégories ne sont pas un champ de `event_registration_settings` :
    // elles ont leurs propres routes et un diff (créer / modifier / supprimer).
    // Elles passent par le même indicateur pour que l'écran n'ait qu'UN état
    // de sauvegarde — deux modèles d'écriture sur un même écran est exactement
    // le piège qu'on supprime ici.
    if ('__categories' in patch) {
      await saveCategoriesRef.current(patch.__categories);
      return;
    }

    const prepare = (v: any) => toISOWithTimezone(v);
    const corps: Record<string, any> = { ...patch };
    for (const cle of ['registration_start_date', 'registration_end_date', 'participant_editing_deadline']) {
      if (cle in corps) corps[cle] = prepare(corps[cle]);
    }

    if (isNewRef.current) {
      // Aucune ligne en base : on crée avec l'état COMPLET de l'écran. Un POST
      // ne portant que le champ modifié laisserait tous les autres aux valeurs
      // par défaut du serveur, pas à celles affichées.
      const complet = { ...formRef.current, ...patch };
      await apiService.createRegistrationSettings(eventId, {
        ...complet,
        registration_start_date: prepare(complet.registration_start_date),
        registration_end_date: prepare(complet.registration_end_date),
        participant_editing_deadline: prepare(complet.participant_editing_deadline),
      });
      isNewRef.current = false;
      setIsNew(false);
      return;
    }

    await apiService.updateRegistrationSettings(eventId, corps);
  }, [eventId]), {
    onError: (err: any) => setError(err?.response?.data?.detail || t('common.error')),
  });

  /** Interrupteur : écrit tout de suite, revient en arrière si le serveur refuse. */
  const basculer = (cle: string, valeur: boolean) => {
    setFormData(f => ({ ...f, [cle]: valeur }));
    setError(null);
    void auto.commit({ [cle]: valeur }, () => setFormData(f => ({ ...f, [cle]: !valeur })));
  };

  // Créneaux : la fenêtre d'ouverture en lot vit ICI, à côté de l'interrupteur qui
  // l'active, et non au planning — c'est en réglant ses inscriptions que
  // l'organisateur décide quels horaires il propose.
  const [heats, setHeats] = useState<Heat[]>([]);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [slotsSaving, setSlotsSaving] = useState(false);

  const chargerHeats = React.useCallback(async () => {
    try {
      const res = await apiService.getEventHeats(eventId);
      setHeats(res.data || []);
    } catch {
      // Pas bloquant : l'interrupteur reste réglable même si le planning ne charge pas.
      setHeats([]);
    }
  }, [eventId]);

  useEffect(() => {
    if (formData.slot_selection_enabled) chargerHeats();
  }, [formData.slot_selection_enabled, chargerHeats]);

  const creneauxOuverts = useMemo(
    () => heats.filter(h => h.slot_open && (h.status || '').toLowerCase() === 'ready').length,
    [heats],
  );

  const appliquerCreneaux = async (openIds: string[], closeIds: string[]) => {
    setSlotsSaving(true);
    try {
      const res = await apiService.bulkUpdateHeatSlots(eventId, openIds, closeIds);
      setSuccess(t('heats.slotsDialog.applied', {
        opened: res.opened_count,
        published: res.published_count,
        closed: res.closed_count,
      }));
      // Une vague verrouillée ou annulée n'a pas bougé : on le DIT, plutôt que de
      // laisser croire que tout a été appliqué (LOG-195).
      if (res.skipped.length > 0) {
        setError(t('heats.slotsDialog.skipped', { count: res.skipped.length }));
      }
      // Une épreuve en brouillon n'a pas de planning public : le créneau serait
      // choisissable à l'inscription sans être visible ailleurs.
      if (res.draft_wods.length > 0) {
        setError(t('heats.slotsDialog.draftWods', { wods: res.draft_wods.join(', ') }));
      }
      setSlotsOpen(false);
      await chargerHeats();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setSlotsSaving(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, [eventId]);

  const loadSettings = async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getRegistrationSettings(eventId);
      const data = response.data;

      setFormData({
        registration_open: data.registration_open || false,
        registration_start_date: utcToLocalInputValue(data.registration_start_date),
        registration_end_date: utcToLocalInputValue(data.registration_end_date),
        max_global_participants: data.max_global_participants ?? null,
        show_registration_count: data.show_registration_count !== false,  // ✅ Point 3
        show_category_spots: data.show_category_spots || false,
        category_spots_threshold: data.category_spots_threshold ?? null,
        require_waiver: data.require_waiver || false,
        waiver_text: data.waiver_text || '',
        custom_fields: data.custom_fields || [],
        allow_participant_editing: data.allow_participant_editing || false,
        slot_selection_enabled: data.slot_selection_enabled || false,
        participant_editing_deadline: utcToLocalInputValue(data.participant_editing_deadline),
        event_instagram: data.event_instagram || '',
      });
      setCommissionPayer(data.commission_payer === 'organizer' ? 'organizer' : 'athlete');
      setCommissionLocked(!!data.commission_payer_locked);

      // Ce que le serveur porte : `commitIfChanged` s'y compare pour ne pas
      // réécrire un champ qu'on a seulement traversé au clavier.
      auto.setBaseline({
        registration_start_date: utcToLocalInputValue(data.registration_start_date),
        registration_end_date: utcToLocalInputValue(data.registration_end_date),
        participant_editing_deadline: utcToLocalInputValue(data.participant_editing_deadline),
        max_global_participants: data.max_global_participants ?? null,
        category_spots_threshold: data.category_spots_threshold ?? null,
        waiver_text: data.waiver_text || '',
        event_instagram: data.event_instagram || '',
      });

      setIsNew(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Pas de paramètres existants, mode création
        setIsNew(true);
      } else {
        setError(err.response?.data?.detail || 'Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const prixEuros = (c: number) =>
    (c / 100).toLocaleString(undefined, { style: 'currency', currency: 'EUR' });

  /** Crée les paramètres s'ils n'existent pas encore, avec l'état affiché. */
  const assurerReglages = async () => {
    if (!isNewRef.current) return;
    const complet = formRef.current;
    await apiService.createRegistrationSettings(eventId, {
      ...complet,
      registration_start_date: toISOWithTimezone(complet.registration_start_date),
      registration_end_date: toISOWithTimezone(complet.registration_end_date),
      participant_editing_deadline: toISOWithTimezone(complet.participant_editing_deadline),
    });
    isNewRef.current = false;
    setIsNew(false);
  };

  const erreurPayeur = (err: any) => {
    if (err?.response?.status === 409) setCommissionLocked(true);
    setError(err?.response?.data?.detail || t('common.error'));
  };

  /** Premier temps : demander au serveur ce que le changement ferait aux prix. */
  const proposerPayeur = async (valeur: CommissionPayer) => {
    if (!eventId || valeur === commissionPayer || commissionSaving) return;
    setCommissionSaving(true);
    setError(null);
    try {
      await assurerReglages();
      const res = await apiService.put(`/events/${eventId}/commission-payer`, {
        commission_payer: valeur,
        preview: true,
      });
      const conversions = res.data.conversions || [];
      if (conversions.length === 0) {
        // Aucun prix à convertir (pas de catégorie payante) : rien à confirmer.
        await appliquerPayeur(valeur);
        return;
      }
      setPendingPayer({ payer: valeur, conversions });
    } catch (err: any) {
      erreurPayeur(err);
    } finally {
      setCommissionSaving(false);
    }
  };

  /** Second temps : appliquer, une fois confirmé. */
  const appliquerPayeur = async (valeur: CommissionPayer) => {
    setCommissionSaving(true);
    setError(null);
    try {
      const res = await apiService.put(`/events/${eventId}/commission-payer`, {
        commission_payer: valeur,
      });
      setCommissionPayer(res.data.commission_payer);
      await loadCategories();
      const n = (res.data.conversions || []).length;
      if (n > 0) toast.success(t('commission.pricesUpdated', { count: n }));
      setPendingPayer(null);
    } catch (err: any) {
      setPendingPayer(null);
      erreurPayeur(err);
    } finally {
      setCommissionSaving(false);
    }
  };

  const loadCategories = async () => {
    if (!eventId) return;

    try {
      const response = await apiService.get(`/events/${eventId}/registration-categories`);
      setCategories(response.data || []);
    } catch (err: any) {
      // Ne pas afficher d'erreur si aucune catégorie (normal au début)
      if (err.response?.status !== 404) {
        console.error('Erreur lors du chargement des catégories:', err);
      }
    }
  };

  const saveCategories = async (liste: RegistrationCategory[] = categoriesRef.current) => {
    if (!eventId) return;

    // Récupérer catégories existantes
    const existingResponse = await apiService.get(`/events/${eventId}/registration-categories`);
    const existingCategories: RegistrationCategory[] = existingResponse.data || [];

    // Supprimer catégories qui n'existent plus
    for (const existing of existingCategories) {
      const stillExists = liste.find(c => c.id === existing.id);
      if (!stillExists) {
        await apiService.delete(`/events/${eventId}/registration-categories/${existing.id}`);
      }
    }

    // Créer/Mettre à jour catégories
    for (const category of liste) {
      const isExisting = existingCategories.find(c => c.id === category.id);

      if (isExisting) {
        // Mise à jour
        await apiService.put(`/events/${eventId}/registration-categories/${category.id}`, {
          name: category.name,
          description: category.description,
          team_size: category.team_size,
          price_amount: category.price_amount,
          price_currency: category.price_currency,
          max_spots: category.max_spots,
          is_active: category.is_active,
          stripe_price_id: category.stripe_price_id,
          display_order: category.display_order,
        });
      } else {
        // Création
        await apiService.post(`/events/${eventId}/registration-categories`, {
          name: category.name,
          description: category.description,
          team_size: category.team_size,
          price_amount: category.price_amount,
          price_currency: category.price_currency,
          max_spots: category.max_spots,
          is_active: category.is_active,
          stripe_price_id: category.stripe_price_id,
          display_order: category.display_order,
        });
      }
    }

    // Recharger les catégories pour avoir les IDs serveur
    await loadCategories();
  };

  saveCategoriesRef.current = saveCategories;

  const handleCancel = () => {
    navigate('/admin/events');
  };

  // ── Helpers conversion timezone ──

  // Convertit une string locale "YYYY-MM-DDTHH:MM" en ISO UTC "YYYY-MM-DDTHH:MM:SS.000Z"
  const toISOWithTimezone = (localDateStr: string | null): string | null => {
    if (!localDateStr) return null;
    const date = new Date(localDateStr); // Interprété comme heure locale
    return date.toISOString(); // Converti en UTC avec suffixe Z
  };

  // Convertit une date UTC (avec ou sans Z/+00:00) en string locale pour input datetime-local
  const utcToLocalInputValue = (utcDateStr: string | null): string | null => {
    if (!utcDateStr) return null;
    // Détecter si la string a déjà un indicateur timezone (Z ou +/-HH:MM)
    const hasTimezoneInfo = /Z$|[+-]\d{2}:\d{2}$/.test(utcDateStr);
    // Si pas de timezone, ajouter Z pour forcer interprétation UTC
    const d = new Date(hasTimezoneInfo ? utcDateStr : utcDateStr + 'Z');
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Calculer l'état actuel des inscriptions
  const computeRegistrationStatusLabel = () => {
    if (!formData.registration_open) return 'Fermées manuellement';

    const now = new Date();
    const start = formData.registration_start_date ? new Date(formData.registration_start_date) : null;
    const end = formData.registration_end_date ? new Date(formData.registration_end_date) : null;

    if (start && now < start) return 'Pas encore ouvertes';
    if (end && now > end) return 'Clôturées (fin de période)';
    return 'Ouvertes';
  };

  // Calcul du ratio goodies/inscription pour avertissement anti-abus
  const goodiesWarning = useMemo(() => {
    // Calculer le total maximum des goodies (options payantes)
    const paidFields = formData.custom_fields.filter(f => f.is_paid && f.price_cents);
    if (paidFields.length === 0) return null;

    // Calculer le prix max des goodies (prix × quantité max par champ)
    const maxGoodiesTotal = paidFields.reduce((total, field) => {
      const price = field.price_cents || 0;
      const maxQty = field.max_quantity || 1;
      return total + (price * maxQty);
    }, 0);

    // Trouver le prix minimum des catégories
    const activeCategories = categories.filter(c => c.is_active && c.price_amount > 0);
    if (activeCategories.length === 0) return null;

    const minCategoryPrice = Math.min(...activeCategories.map(c => c.price_amount));

    // Vérifier si le ratio dépasse 2x
    if (maxGoodiesTotal > minCategoryPrice * 2) {
      return {
        goodiesTotal: maxGoodiesTotal / 100, // Convertir en euros
        minPrice: minCategoryPrice / 100,
        ratio: (maxGoodiesTotal / minCategoryPrice).toFixed(1)
      };
    }

    return null;
  }, [formData.custom_fields, categories]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
      <div className={embedded ? "space-y-6" : "p-6 space-y-6"}>
        {/* Header - hidden when embedded in tabs */}
        {!embedded && (
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/events')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-semibold text-foreground">
              Configuration des Inscriptions
            </h1>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <button onClick={() => setError(null)} className="text-sm underline ml-4">Fermer</button>
            </AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            <AlertDescription className="flex items-center justify-between">
              {success}
              <button onClick={() => setSuccess(null)} className="text-sm underline ml-4">Fermer</button>
            </AlertDescription>
          </Alert>
        )}

        {/* --- GÉNÉRAL --- */}
        {section === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Colonne gauche : Paramètres généraux */}
          <Card>
            <CardHeader>
              <CardTitle>Paramètres généraux d'inscription</CardTitle>
              <CardDescription>
                Activez ou désactivez les inscriptions manuellement et définissez la période d'ouverture.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Switch Inscriptions ouvertes avec tooltip */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id="registration_open"
                    checked={formData.registration_open}
                    onCheckedChange={(checked) => basculer('registration_open', checked)}
                  />
                  <Label htmlFor="registration_open">Inscriptions ouvertes</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Si désactivé, les inscriptions sont fermées même si la période ci-dessous est en cours.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-xs text-muted-foreground ml-10">
                  État actuel : {computeRegistrationStatusLabel()}
                </p>
              </div>

              {/* DateTimePickers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date et heure de début</Label>
                  <DateTimeInput
                    id="start_date"
                    type="datetime-local"
                    value={formData.registration_start_date || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_start_date: e.target.value || null })
                    }
                    onBlur={(e) => {
                      if (saisieDateIncomplete(e)) return;
                      void auto.commitIfChanged('registration_start_date', e.target.value || null);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">Laisser vide pour démarrage immédiat</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Date et heure de fin</Label>
                  <DateTimeInput
                    id="end_date"
                    type="datetime-local"
                    value={formData.registration_end_date || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, registration_end_date: e.target.value || null })
                    }
                    onBlur={(e) => {
                      if (saisieDateIncomplete(e)) return;
                      void auto.commitIfChanged('registration_end_date', e.target.value || null);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">Laisser vide pour aucune limite</p>
                </div>
              </div>

              {/* Participants maximum */}
              <div className="space-y-2">
                <Label htmlFor="max_participants">Participants maximum</Label>
                <NumberInput
                  id="max_participants"
                  value={formData.max_global_participants ?? ''}
                  min={1}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_global_participants: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  onBlur={(e) =>
                    void auto.commitIfChanged(
                      'max_global_participants',
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                />
                <p className="text-xs text-muted-foreground">Laisser vide pour illimité</p>
              </div>

              {/* ✅ Point 3: Affichage du compteur d'inscriptions */}
              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show_registration_count"
                    checked={formData.show_registration_count}
                    onCheckedChange={(checked) => basculer('show_registration_count', checked)}
                  />
                  <Label htmlFor="show_registration_count">Afficher le compteur d'inscriptions</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Si activé, le nombre d'inscrits et de places disponibles sera visible sur la page publique de l'événement.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              {/* Places restantes par catégorie : « 8 places restantes » sous
                  chaque catégorie limitée. « Complet » s'affiche toujours. */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-start gap-2">
                  <Switch
                    id="show_category_spots"
                    checked={formData.show_category_spots}
                    onCheckedChange={(checked) => basculer('show_category_spots', checked)}
                  />
                  <div>
                    <Label htmlFor="show_category_spots">{t('categorySpots.label')}</Label>
                    <p className="text-xs text-muted-foreground">{t('categorySpots.hint')}</p>
                  </div>
                </div>

                {formData.show_category_spots && (
                  <div className="ml-11 pl-4 border-l border-border space-y-2">
                    <div className="flex items-start gap-2">
                      <Switch
                        id="category_spots_threshold_on"
                        checked={formData.category_spots_threshold !== null}
                        onCheckedChange={(checked) => {
                          const avant = formData.category_spots_threshold;
                          const apres = checked ? 5 : null;
                          setFormData(f => ({ ...f, category_spots_threshold: apres }));
                          setError(null);
                          void auto.commit({ category_spots_threshold: apres }, () =>
                            setFormData(f => ({ ...f, category_spots_threshold: avant })),
                          );
                        }}
                      />
                      <div>
                        <Label htmlFor="category_spots_threshold_on">{t('categorySpots.thresholdLabel')}</Label>
                        <p className="text-xs text-muted-foreground">{t('categorySpots.thresholdHint')}</p>
                      </div>
                    </div>
                    {formData.category_spots_threshold !== null && (
                      <div className="flex items-center gap-2 ml-11">
                        <Label htmlFor="category_spots_threshold" className="text-sm font-normal">
                          {t('categorySpots.thresholdFrom')}
                        </Label>
                        <NumberInput
                          id="category_spots_threshold"
                          className="w-20"
                          min={1}
                          value={formData.category_spots_threshold ?? ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              category_spots_threshold: e.target.value ? parseInt(e.target.value) : null,
                            })
                          }
                          onBlur={(e) => {
                            const n = parseInt(e.target.value);
                            if (!n || n < 1) return;
                            void auto.commitIfChanged('category_spots_threshold', n);
                          }}
                        />
                        <span className="text-sm text-muted-foreground">{t('categorySpots.thresholdUnit')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {eventId && <OrganizerEmailModeField eventId={eventId} />}

              {/* Créneaux : l'athlète choisit sa vague en s'inscrivant */}
              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <Switch
                    id="slot_selection_enabled"
                    checked={formData.slot_selection_enabled}
                    onCheckedChange={(checked) => basculer('slot_selection_enabled', checked)}
                  />
                  <Label htmlFor="slot_selection_enabled">Faire choisir un créneau à l'inscription</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Une étape s'ajoute au formulaire : l'athlète choisit son horaire parmi les
                        vagues que vous avez publiées ET ouvertes à l'inscription (planning, colonne
                        « Inscription »). Les vagues arrivent ainsi pré-remplies le jour J.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {formData.slot_selection_enabled && (
                  <div className="space-y-3 pt-1">
                    {/* L'état ET l'action au même endroit que la décision. Avant, il
                        fallait aller au planning, publier, puis cliquer chaque porte —
                        deux gestes sur un autre écran, et rien ne le disait. */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="space-y-1">
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          <DoorOpen className="h-4 w-4 text-success" />
                          {t('heats.slotsDialog.openCount', {
                            open: creneauxOuverts,
                            total: heats.length,
                          })}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          {t('heats.slotsDialog.onlyOpenChoosable')}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSlotsOpen(true)}
                        disabled={heats.length === 0}
                      >
                        <DoorOpen className="mr-2 h-4 w-4" />
                        {t('heats.slotsDialog.manage')}
                      </Button>
                    </div>
                    {heats.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t('heats.slotsDialog.noWaves')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Colonne droite : modification + communication empilées */}
          <div className="space-y-6">
          {/* Modification des participants */}
          <Card>
            <CardHeader>
              <CardTitle>Modification des participants</CardTitle>
              <CardDescription>
                Autorisez les équipes inscrites à modifier les noms, emails et téléphones de leurs participants après inscription.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="allow_participant_editing"
                  checked={formData.allow_participant_editing}
                  onCheckedChange={(checked) => basculer('allow_participant_editing', checked)}
                />
                <Label htmlFor="allow_participant_editing">Autoriser la modification des participants</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Si activé, les participants peuvent modifier les noms, emails et téléphones depuis leur tableau de bord.</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {formData.allow_participant_editing && (
                <div className="space-y-2">
                  <Label htmlFor="participant_editing_deadline">Date limite de modification</Label>
                  <DateTimeInput
                    id="participant_editing_deadline"
                    type="datetime-local"
                    value={formData.participant_editing_deadline || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, participant_editing_deadline: e.target.value || null })
                    }
                    onBlur={(e) => {
                      if (saisieDateIncomplete(e)) return;
                      void auto.commitIfChanged('participant_editing_deadline', e.target.value || null);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Laisser vide pour permettre la modification sans limite de temps
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informations pour la communication */}
          <Card>
            <CardHeader>
              <CardTitle>Informations pour la communication</CardTitle>
              <CardDescription>
                Coordonnées de communication de l'événement, utilisées dans les emails aux participants.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="event_instagram">Compte Instagram de l'événement (optionnel)</Label>
              <Input
                id="event_instagram"
                value={formData.event_instagram}
                onChange={(e) => setFormData({ ...formData, event_instagram: e.target.value })}
                onBlur={(e) => void auto.commitIfChanged('event_instagram', e.target.value)}
                placeholder="@compte_evenement"
              />
              <p className="text-xs text-muted-foreground">
                Utilisé dans les emails d'inscription (concours) : les participants seront invités à suivre et identifier ce compte, en plus de @logfit.eu.
              </p>
            </CardContent>
          </Card>
          </div>
        </div>
        )}

        {/* --- CATÉGORIES --- */}
        {section === 'categories' && (
          <Card>
            <CardHeader>
              <CardTitle>Catégories d'inscription</CardTitle>
              <CardDescription>
                Définissez les différentes catégories d'inscription avec leurs tarifs et nombre de participants. Ex: RX Solo (1 participant, 50€), RX Duo (2 participants, 90€), Scaled Team (3 participants, 120€).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div>
                  <Label className="text-base">{t('commission.title')}</Label>
                  <p className="text-xs text-muted-foreground">{t('commission.hint')}</p>
                </div>
                <RadioGroup
                  value={commissionPayer}
                  onValueChange={(v) => void proposerPayeur(v as CommissionPayer)}
                  disabled={commissionLocked || commissionSaving}
                >
                  {(['athlete', 'organizer'] as const).map((valeur) => (
                    <label
                      key={valeur}
                      htmlFor={`commission_${valeur}`}
                      className={`flex items-start gap-3 rounded-md border p-3 ${
                        commissionPayer === valeur ? 'border-foreground/40' : 'border-border'
                      } ${commissionLocked ? 'opacity-70' : 'cursor-pointer'}`}
                    >
                      <RadioGroupItem value={valeur} id={`commission_${valeur}`} className="mt-1 border-foreground text-foreground" />
                      <span>
                        <span className="block text-sm font-medium">{t(`commission.${valeur}`)}</span>
                        <span className="block text-xs text-muted-foreground">{t(`commission.${valeur}Hint`)}</span>
                      </span>
                    </label>
                  ))}
                </RadioGroup>
                {commissionLocked ? (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    {t('commission.locked')}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('commission.lockHint')}</p>
                )}
              </div>
              <Dialog open={!!pendingPayer} onOpenChange={(o) => { if (!o && !commissionSaving) setPendingPayer(null); }}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {pendingPayer?.payer === 'organizer'
                        ? t('commission.confirmTitleOrganizer')
                        : t('commission.confirmTitleAthlete')}
                    </DialogTitle>
                    <DialogDescription>{t('commission.confirmText')}</DialogDescription>
                  </DialogHeader>
                  <ul className="divide-y divide-border text-sm">
                    {pendingPayer?.conversions.map((c) => (
                      <li key={c.name} className="flex items-center justify-between gap-4 py-2">
                        <span>{c.name}</span>
                        <span className="whitespace-nowrap">
                          <span className="text-muted-foreground line-through">{prixEuros(c.old_price_cents)}</span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="font-medium">{prixEuros(c.new_price_cents)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPendingPayer(null)} disabled={commissionSaving}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={() => pendingPayer && void appliquerPayeur(pendingPayer.payer)}
                      disabled={commissionSaving}
                    >
                      {commissionSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('commission.apply')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <RegistrationCategoryBuilder
                eventId={eventId}
                commissionPayer={commissionPayer}
                categories={categories}
                onChange={(liste) => {
                  setCategories(liste);
                  setError(null);
                  // Le builder a ses propres fenêtres : valider l'une d'elles
                  // EST le geste explicite, on écrit derrière.
                  void auto.commit({ __categories: liste });
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* --- QUESTIONS & DÉCHARGE --- */}
        {section === 'questions' && (
        <div className="space-y-6">
          {/* Décharge de responsabilité */}
          <Card>
            <CardHeader>
              <CardTitle>Décharge de responsabilité (Waiver)</CardTitle>
              <CardDescription>
                Texte affiché lors de l'inscription et devant être accepté par le participant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="require_waiver"
                  checked={formData.require_waiver}
                  onCheckedChange={(checked) => basculer('require_waiver', checked)}
                />
                <Label htmlFor="require_waiver">Requérir une décharge de responsabilité</Label>
              </div>
              {formData.require_waiver && (
                <div className="space-y-2">
                  <Label htmlFor="waiver_text">Texte de la décharge</Label>
                  <Textarea
                    id="waiver_text"
                    rows={8}
                    value={formData.waiver_text}
                    onChange={(e) =>
                      setFormData({ ...formData, waiver_text: e.target.value })
                    }
                    onBlur={(e) => void auto.commitIfChanged('waiver_text', e.target.value)}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce texte sera affiché lors de l'inscription et devra être accepté par les participants
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Champs personnalisés */}
          <Card>
            <CardHeader>
              <CardTitle>Champs personnalisés</CardTitle>
              <CardDescription>
                Questions posées aux participants lors de l'inscription.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CustomFieldBuilder
                fields={formData.custom_fields}
                onChange={(fields) => {
                  setFormData({ ...formData, custom_fields: fields });
                  setError(null);
                  void auto.commit({ custom_fields: fields });
                }}
              />

              {/* Avertissement anti-abus pour options payantes */}
              {goodiesWarning && (
                <Alert variant="warning" className="mt-4">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertDescription>
                    <strong>Attention :</strong> Le total maximum des options payantes ({goodiesWarning.goodiesTotal.toFixed(2)}€)
                    représente {goodiesWarning.ratio}x le prix d'inscription minimum ({goodiesWarning.minPrice.toFixed(2)}€).
                    <br />
                    <span className="text-muted-foreground text-sm">
                      Rappel : LogFit® est une plateforme d'inscription aux événements sportifs.
                      La vente d'articles doit rester accessoire à l'inscription.
                      Un ratio élevé peut entraîner une révision de ton compte.
                    </span>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        {/* L'écran s'enregistre tout seul : le bouton est remplacé par l'ÉTAT
            de la sauvegarde. « Annuler » a disparu avec lui — il ne pouvait
            plus rien annuler, et promettre une marche arrière qui n'existe pas
            est pire que la friction qu'on supprime. */}
        <div className="mt-8 pt-6 border-t border-border flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{t('autoSave.hint')}</span>
          <div className="flex items-center gap-3">
            <AutoSaveIndicator status={auto.status} retry={auto.retry} />
            <Button variant="outline" onClick={handleCancel}>
              {t('common.back')}
            </Button>
          </div>
        </div>

        {/* Monté au niveau du composant, pas dans le panneau : un Dialog démonté
            au changement d'onglet se refermerait tout seul. */}
        <SlotsDialog
          open={slotsOpen}
          onOpenChange={setSlotsOpen}
          heats={heats}
          loading={slotsSaving}
          onConfirm={appliquerCreneaux}
        />
      </div>
  );
};

// Wrapper page pour la route /events/:eventId/registration (rétrocompatibilité)
const EventRegistrationSettings: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();

  if (!eventId) return null;

  return <RegistrationSettingsContent eventId={eventId} />;
};

export default EventRegistrationSettings;
